/**
 * Base error class for all database errors.
 * Exposes the underlying PG error code (e.g. '23505' for unique violation)
 * so callers can branch on it without inspecting the cause chain.
 *
 * Well-known codes:
 *   'NOT_FOUND'       — executeOneOrThrow returned no rows
 *   'DATA_INTEGRITY'  — executeOne / executeOneOrThrow returned more than one row
 */
export class DbError extends Error {
  readonly code: string | undefined

  constructor(message: string, options?: { cause?: Error; code?: string }) {
    super(message, { cause: options?.cause })
    this.name = 'DbError'
    this.code = options?.code
  }
}

/**
 * Wraps a raw database error in a DbError, preserving the PG error code.
 * Use in adapter catch blocks to normalize errors.
 */
export function wrapPgError(err: unknown): never {
  if (err instanceof DbError) throw err
  if (err instanceof Error) {
    const code = (err as { code?: string }).code
    throw new DbError(err.message, { cause: err, code })
  }
  throw err
}
